import React, { useMemo } from 'react';
import {
  Activity,
  CalendarClock,
  ExternalLink,
  Hash,
  MessageCircle,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Code2,
} from 'lucide-react';
import type { ForumBase } from '../types/forum';
import {
  aggregateCategoryForums,
  formatCategoryLastUpdate,
  latestForumUpdateIso,
} from '../utils/categoryAggregates';
import { buildSoftwareSeoParagraph } from '../utils/softwareAggregates';
import { formatChangePercent, statDelta } from '../utils/forumStatChange';
import { appendOutboundUtm } from '../utils/outboundUrl';

interface Props {
  softwareName: string;
  softwareDescription?: string | null;
  websiteUrl?: string | null;
  forums: ForumBase[];
  darkMode: boolean;
  marketSharePercent?: number | null;
}

const SoftwareSummaryPanel: React.FC<Props> = ({
  softwareName,
  softwareDescription,
  websiteUrl,
  forums,
  darkMode,
  marketSharePercent,
}) => {
  const agg = useMemo(() => aggregateCategoryForums(forums), [forums]);
  const lastUpdateIso = useMemo(() => latestForumUpdateIso(forums), [forums]);
  const seoParagraph = useMemo(
    () =>
      buildSoftwareSeoParagraph(
        softwareName,
        agg,
        lastUpdateIso,
        softwareDescription,
        marketSharePercent
      ),
    [softwareName, agg, lastUpdateIso, softwareDescription, marketSharePercent]
  );

  const card = darkMode
    ? 'bg-gray-800/80 border border-gray-700'
    : 'bg-white shadow-sm border border-violet-100';

  const statRow = (label: string, current: number, previous: number, icon: React.ReactNode) => {
    const delta = statDelta(current, previous);
    const pct = formatChangePercent(current, previous);
    return (
      <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-200/60 dark:border-gray-700/80 last:border-0">
        <div className="flex items-center gap-2 min-w-0">
          {icon}
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{label}</span>
        </div>
        <div className="text-right shrink-0">
          <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {current.toLocaleString('tr-TR')}
          </span>
          {delta !== null && pct && (
            <span
              className={`ml-2 text-xs inline-flex items-center gap-0.5 ${
                delta > 0 ? 'text-green-500' : delta < 0 ? 'text-red-500' : 'text-gray-400'
              }`}
            >
              {delta > 0 ? <ArrowUpRight size={12} /> : delta < 0 ? <ArrowDownRight size={12} /> : <Minus size={12} />}
              {pct}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <section
      className={`rounded-xl p-4 sm:p-6 h-full ${card}`}
      aria-labelledby="software-summary-heading"
    >
      <h2
        id="software-summary-heading"
        className={`text-lg font-semibold mb-1 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-violet-900'}`}
      >
        <Code2 size={20} className="text-violet-500 shrink-0" />
        Yazılım özeti
      </h2>
      <p className={`text-base font-medium mb-2 break-words ${darkMode ? 'text-violet-300' : 'text-violet-800'}`}>
        {softwareName}
      </p>
      {marketSharePercent != null && marketSharePercent > 0 && (
        <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Aktif forumların ~%{marketSharePercent.toFixed(1)} pazar payı
        </p>
      )}
      <p className={`text-sm mb-4 flex items-center gap-2 flex-wrap ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <CalendarClock size={16} className="shrink-0" />
        Son güncelleme: {formatCategoryLastUpdate(lastUpdateIso)}
      </p>

      <div className="mb-4">
        <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-200/60 dark:border-gray-700/80">
          <div className="flex items-center gap-2">
            <Hash className="text-purple-500 shrink-0" size={18} />
            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Forum sayısı</span>
          </div>
          <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {agg.forumCount.toLocaleString('tr-TR')}
          </span>
        </div>
        {statRow(
          'Toplam üye',
          agg.totalMembers,
          agg.previousTotalMembers,
          <Users className="text-blue-500 shrink-0" size={18} />
        )}
        {statRow(
          'Toplam konu',
          agg.totalTopics,
          agg.previousTotalTopics,
          <MessageCircle className="text-purple-500 shrink-0" size={18} />
        )}
        {statRow(
          'Toplam ileti',
          agg.totalPosts,
          agg.previousTotalPosts,
          <Activity className="text-green-500 shrink-0" size={18} />
        )}
      </div>

      {websiteUrl && (
        <a
          href={
            appendOutboundUtm(websiteUrl) ?? websiteUrl
          }
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-1.5 text-sm font-medium mb-3 ${
            darkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-800'
          }`}
        >
          <ExternalLink size={14} />
          Resmi site
        </a>
      )}

      <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {seoParagraph}
      </p>
    </section>
  );
};

export default SoftwareSummaryPanel;
